import { Box, Typography, Button, Container, Grid, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { Link } from "react-router-dom";

const Home = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
      title: "Save Time",
      description:
        "Automate tedious bookkeeping tasks and focus on what matters most",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: "Save Money",
      description:
        "Reduce accounting costs by up to 90% with intelligent automation",
    },
    {
      icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
      title: "Stay Compliant",
      description: "Maintain audit-ready financials with real-time accuracy",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.background.default}, ${theme.palette.background.paper})`,
        pt: { xs: 8, md: 12 },
        pb: 8,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left Content Section */}
          <Grid item xs={12} md={6}>
            <Stack spacing={4}>
              {/* Main Headline */}
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 2,
                    fontSize: { xs: "2rem", md: "3rem" },
                  }}
                >
                  Future of Finance
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    fontWeight: 400,
                    fontSize: { xs: "1rem", md: "1.1rem" },
                  }}
                >
                  Still wasting hours on accounts? Focus on building your
                  business. Let{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: theme.palette.primary.main }}
                  >
                    f-ai
                  </Box>{" "}
                  handle your accounting and save up to{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: theme.palette.success.main }}
                  >
                    90%
                  </Box>{" "}
                  of your bookkeeping expenses.
                </Typography>
              </Box>

              {/* CTA Buttons */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  size="large"
                  color="primary"
                  sx={{
                    borderRadius: 2,
                    px: 6,
                    py: 2,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: theme.shadows[8],
                    "&:hover": {
                      boxShadow: theme.shadows[12],
                      transform: "translateY(-2px)",
                      transition: "all 0.3s ease",
                    },
                  }}
                >
                  Get Started
                </Button>
                <Button
                  component={Link}
                  to="/Contactus"
                  variant="outlined"
                  size="large"
                  color="primary"
                  sx={{
                    borderRadius: 2,
                    px: 6,
                    py: 2,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    borderWidth: 2,
                    "&:hover": {
                      borderWidth: 2,
                      transform: "translateY(-2px)",
                      transition: "all 0.3s ease",
                    },
                  }}
                >
                  Get a Demo
                </Button>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
              }}
            >
              <Box
                component="img"
                src="/HomeLogo.png"
                alt="Financial Dashboard"
                sx={{
                  width: "100%",
                  maxWidth: 500,
                  height: "auto",
                  borderRadius: 3,
                  boxShadow: theme.shadows[10],
                }}
              />

              <Box
                component="img"
                src="/HomeLogo2.png"
                alt="Financial Dashboard"
                sx={{
                  position: "absolute",
                  bottom: { xs: -10, sm: -15, md: -20 },
                  right: { xs: -10, sm: -15, md: -20 },
                  width: { xs: 60, sm: 80, md: 100 },
                  height: { xs: 60, sm: 80, md: 100 },
                  borderRadius: "50%",
                  boxShadow: theme.shadows[10],
                  objectFit: "cover",
                  border: "4px solid white",
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Feature Cards */}
        <Grid container spacing={4} sx={{ mt: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box
                sx={{
                  p: 4,
                  height: "100%",
                  borderRadius: 2,
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: theme.shadows[8],
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <Box
                  sx={{
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;
